/**
 * 📄 HOOK GESTION FACTURES LOGISTICS
 * ===================================
 * Hook custom pour la gestion manuelle des factures clients logistics
 *
 * Workflow:
 * 1. Utilisateur sélectionne lignes Job Costing (revenue/disbursement)
 * 2. Clique "Générer facture" → Modal de génération
 * 3. Validation → Création facture avec items sélectionnés
 * 4. Téléchargement PDF (backend React-PDF)
 * 5. Enregistrement paiements optionnel
 *
 * Différence avec souscription:
 * - ❌ Pas d'automatisation (pas de génération auto périodique)
 * - ✅ Génération manuelle uniquement
 * - ✅ Basé sur sélection utilisateur (lignes revenue/disbursement)
 *
 * @see routes/logistics/invoices.js (backend déjà existant)
 * @see src/gestion-souscription/components/InvoiceList.tsx (pattern PDF)
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { message } from 'antd';
import API_CONFIG from '../../config/api';
import { Invoice, GenerateInvoiceRequest, PaymentRecord } from '../types/invoice.types';
import { useSelection } from '../../SelectionContext';

export const useInvoices = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [currentInvoice, setCurrentInvoice] = useState<Invoice | null>(null);

  const { selectedCompany } = useSelection();

  /**
   * Récupérer toutes les factures d'un dossier spécifique
   * @param shipmentId - ID du dossier d'expédition
   */
  const fetchInvoicesByShipment = useCallback(async (shipmentId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/invoices`,
        {
          params: { shipment_id: shipmentId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        // Backend retourne { success, data: [...] } où data est l'array directement
        const invoicesList = response.data.data || [];
        setInvoices(invoicesList);
        console.log('✅ [useInvoices] Factures chargées:', invoicesList.length);
        return invoicesList;
      } else {
        message.error(response.data.message || 'Erreur lors du chargement des factures');
        return [];
      }
    } catch (error: any) {
      console.error('❌ [useInvoices] Erreur fetchInvoicesByShipment:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion au serveur');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupérer les détails complets d'une facture (avec items)
   * @param invoiceId - ID de la facture
   */
  const fetchInvoiceById = useCallback(async (invoiceId: number): Promise<Invoice | null> => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/invoices/${invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        // Backend retourne { success, data: {...} } où data est l'object invoice directement
        const invoice = response.data.data;
        setCurrentInvoice(invoice);
        console.log('✅ [useInvoices] Facture détaillée chargée:', invoice.invoice_number);
        return invoice;
      } else {
        message.error(response.data.message || 'Erreur lors du chargement de la facture');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [useInvoices] Erreur fetchInvoiceById:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Générer une facture à partir de lignes Job Costing sélectionnées
   * (Workflow manuel : utilisateur sélectionne lignes → génère facture)
   *
   * @param data - Données de génération (shipment_id, job_costing_line_ids, client_name, etc.)
   */
  const generateInvoiceFromJobCosting = useCallback(async (
    data: GenerateInvoiceRequest
  ): Promise<Invoice | null> => {
    setLoading(true);
    try {
      console.log('📄 [useInvoices] Génération facture depuis Job Costing:', data);

      const response = await axios.post(
        `${API_CONFIG.API_URL}/logistics/invoices/generate`,
        {
          ...data,
          company_id: selectedCompany, // SuperAdmin context
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        // Backend retourne { success, message, data: {...} } où data est l'invoice directement
        const newInvoice = response.data.data;
        message.success(`Facture ${newInvoice.invoice_number} générée avec succès`);

        // Ajouter la nouvelle facture à la liste
        setInvoices((prev) => [newInvoice, ...prev]);
        setCurrentInvoice(newInvoice);

        console.log('✅ [useInvoices] Facture créée:', newInvoice.invoice_number);
        return newInvoice;
      } else {
        message.error(response.data.message || 'Erreur lors de la génération');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [useInvoices] Erreur generateInvoiceFromJobCosting:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la génération de la facture');
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  /**
   * Convertir une proforma en facture definitive
   * @param invoiceId - ID de la proforma
   */
  const convertProformaToInvoice = useCallback(async (
    invoiceId: number
  ): Promise<{ proforma: Invoice; invoice: Invoice } | null> => {
    setLoading(true);
    try {
      console.log('[useInvoices] Conversion proforma:', invoiceId);

      const response = await axios.post(
        `${API_CONFIG.API_URL}/logistics/invoices/${invoiceId}/convert-to-final`,
        {
          company_id: selectedCompany,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        message.success('Proforma convertie en facture definitive');

        setInvoices((prev) => {
          const updated = prev.map((inv) =>
            inv.id === data.proforma.id ? { ...inv, ...data.proforma } : inv
          );
          const hasFinal = updated.some((inv) => inv.id === data.invoice.id);
          return hasFinal ? updated : [data.invoice, ...updated];
        });

        setCurrentInvoice(data.invoice);
        return data;
      }

      message.error(response.data.message || 'Erreur lors de la conversion');
      return null;
    } catch (error: any) {
      console.error('[useInvoices] Erreur conversion proforma:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la conversion de la proforma');
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  /**
   * Télécharger le PDF d'une facture
   * Pattern identique au module souscription (backend React-PDF)
   *
   * @param invoiceId - ID de la facture
   * @param invoiceNumber - Numéro de facture (pour le nom du fichier)
   */
  const downloadInvoicePDF = useCallback(async (
    invoiceId: number,
    invoiceNumber: string
  ): Promise<void> => {
    try {
      console.log('📥 [useInvoices] Téléchargement PDF:', { invoiceId, invoiceNumber });

      // Étape 1: Récupérer les données complètes de la facture
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/invoices/${invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la récupération de la facture');
      }

      const invoiceData = response.data.data;

      // Étape 2: Préparer l'objet warehouse (émetteur de la facture)
      // Dans logistics, c'est l'entreprise de l'utilisateur qui émet la facture
      // Les champs company_* sont retournés par le backend via JOIN avec companies
      const warehouse = {
        name: invoiceData.company_name || 'Entreprise',
        phone: invoiceData.company_phone || '',
        email: invoiceData.company_email || '',
        address: invoiceData.company_address || '',
        logo: invoiceData.company_logo || '',
      };

      // Étape 2bis: Préparer l'objet client (facturé à)
      // Les champs client_* sont retournés par le backend via JOIN avec users + user_details
      const client = {
        id: invoiceData.client_id,
        name: invoiceData.client_name || 'Client',
        phone: invoiceData.client_phone || '',
        email: invoiceData.client_email || '',
        address: invoiceData.client_address || '',
        tax_number: invoiceData.client_tax_number || '',
        rccm: invoiceData.client_rccm || '',
        ifu: invoiceData.client_ifu || '',
      };

      const isProforma = invoiceData.invoice_type === 'proforma';
      const fileName = `${isProforma ? 'Proforma' : 'Facture'}-${invoiceNumber}.pdf`;

      // Étape 3: Appeler l'API backend React-PDF (PATTERN EXACT SOUSCRIPTION)
      message.loading({ content: 'Génération du PDF...', key: 'pdf-gen', duration: 0 });

      const pdfResponse = await axios.post(
        `${API_CONFIG.API_URL}/logistics/invoices/generate-pdf-react`,
        {
          invoiceData: {
            ...invoiceData,
            warehouse,
            client, // Ajout de l'objet client avec rccm et ifu
          },
          fileName,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob', // Important pour recevoir le PDF en blob
        }
      );

      // Étape 4: Télécharger le fichier PDF
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Facture téléchargée avec succès', key: 'pdf-gen' });
      console.log('✅ [useInvoices] PDF téléchargé avec succès');
    } catch (error: any) {
      console.error('❌ [useInvoices] Erreur downloadInvoicePDF:', error);
      message.error({
        content: error.response?.data?.message || 'Erreur lors de la génération du PDF',
        key: 'pdf-gen',
      });
    }
  }, []);

  /**
   * Enregistrer un paiement sur une facture
   * @param invoiceId - ID de la facture
   * @param paymentData - Données du paiement (amount, payment_date, payment_method, etc.)
   */
  const recordPayment = useCallback(async (
    invoiceId: number,
    paymentData: PaymentRecord
  ): Promise<boolean> => {
    setLoading(true);
    try {
      console.log('💰 [useInvoices] Enregistrement paiement:', { invoiceId, paymentData });

      const response = await axios.post(
        `${API_CONFIG.API_URL}/logistics/invoices/${invoiceId}/payments`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        message.success('Paiement enregistré avec succès');

        // Backend retourne { success, message, invoice: {...}, payment: {...} }
        // La facture mise à jour est dans response.data.invoice (pas dans data.data)
        const updatedInvoice = response.data.invoice;

        // Mettre à jour la facture dans la liste
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id === invoiceId
              ? { ...inv, ...updatedInvoice }
              : inv
          )
        );

        console.log('✅ [useInvoices] Paiement enregistré');
        return true;
      } else {
        message.error(response.data.message || 'Erreur lors de l\'enregistrement');
        return false;
      }
    } catch (error: any) {
      console.error('❌ [useInvoices] Erreur recordPayment:', error);
      message.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Supprimer une facture
   * @param invoiceId - ID de la facture à supprimer
   */
  const deleteInvoice = useCallback(async (invoiceId: number): Promise<boolean> => {
    setLoading(true);
    try {
      console.log('🗑️ [useInvoices] Suppression facture:', invoiceId);

      const response = await axios.delete(
        `${API_CONFIG.API_URL}/logistics/invoices/${invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        message.success('Facture supprimée avec succès');

        // Retirer la facture de la liste
        setInvoices((prev) => prev.filter((inv) => inv.id !== invoiceId));

        console.log('✅ [useInvoices] Facture supprimée');
        return true;
      } else {
        message.error(response.data.message || 'Erreur lors de la suppression');
        return false;
      }
    } catch (error: any) {
      console.error('❌ [useInvoices] Erreur deleteInvoice:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // État
    loading,
    invoices,
    currentInvoice,

    // Actions
    fetchInvoicesByShipment,
    fetchInvoiceById,
    generateInvoiceFromJobCosting,
    convertProformaToInvoice,
    downloadInvoicePDF,
    recordPayment,
    deleteInvoice,
  };
};

export default useInvoices;
